import React, { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { ContactType } from 'flow/utils/extension-utils';
import { isCallRecordAMeeting } from 'flow/utils/call-records-utils';
import {
    makeSelectExtensionKeyFromContactKey,
    selectExtensionStatus,
} from 'flow/selectors/ExtensionSelectors';
import { selectCallRecordChatSession } from 'flow/selectors/call-selectors';
import { useCallRecordWithCtiContactByKey } from 'flow/hooks/useCallRecordWithCtiContact';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import ChatAvatar, {
    AvatarSize,
} from 'flow/components/ui-components/ChatAvatar';

interface Props {
    callRecordKey: CallRecordKey;
    selected?: boolean;
    size?: AvatarSize;
    hideContactAvatarWhenNoResource?: boolean;
}

const CallRecordAvatar = ({
    callRecordKey,
    selected,
    size = 'medium',
    hideContactAvatarWhenNoResource,
}: Props) => {
    const callRecord = useCallRecordWithCtiContactByKey(callRecordKey);

    const isVideoCall = callRecord ? isCallRecordAMeeting(callRecord) : false;

    const contactKey = callRecord?.getIn(['contact', 'key']);
    const contactIsBoundExtension = ContactType.bound.is(
        callRecord?.getIn(['contact', 'type']),
    );

    const selectExtensionKey = useMemo(
        makeSelectExtensionKeyFromContactKey,
        [],
    );
    const extensionKey = useAppSelector((state) =>
        contactIsBoundExtension
            ? selectExtensionKey(state, contactKey)
            : undefined,
    );

    const status = useAppSelector((state) =>
        selectExtensionStatus(state, extensionKey),
    );

    const chatSession = useAppSelector((state) =>
        selectCallRecordChatSession(state, callRecordKey),
    );

    if (!callRecord) return null;

    const fallBackBackgroundColorClass = twMerge(
        'group-hover:bg-gray-300',
        selected ? 'bg-gray-300' : 'bg-gray-200',
    );

    if (isVideoCall) {
        // Before the linked chat session has loaded we don't have room image or
        // member info to render — fall back to the generic "person" avatar
        // rather than ChatAvatar's gray skeleton placeholder.
        if (!chatSession) {
            return (
                <ContactAvatar
                    size={size}
                    fallBackBackgroundColorClass={fallBackBackgroundColorClass}
                />
            );
        }
        return (
            <ChatAvatar
                session={chatSession}
                size={size}
                showUnreadBadge={false}
            />
        );
    }

    const contact = callRecord.get('contact');
    if (hideContactAvatarWhenNoResource && !contact?.get('avatarResource')) {
        return null;
    }

    return (
        <ContactAvatar
            contact={contact}
            status={status}
            size={size}
            fallBackBackgroundColorClass={fallBackBackgroundColorClass}
        />
    );
};

export default React.memo(CallRecordAvatar);
